import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BulkFillReport,
  SpreadsheetSelectionEvent,
  SpreadsheetSelectionSnapshot,
} from 'components/ScalarDatasheet/ScalarDatasheet.props';
import { EventType, useEventBus } from 'context';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { SpreadsheetSelectionAction } from '../components/SelectionActionsBar/SelectionActionsBar.props';

const NO_ACTIONS: SpreadsheetSelectionAction[] = [];

const sameNumbers = (a: number[], b: number[]) => a.length === b.length && a.every((n, i) => n === b[i]);

/** True when both snapshots cover the same cells, whatever state those cells are in. */
export const isSameSelection = (
  a: SpreadsheetSelectionSnapshot | null,
  b: SpreadsheetSelectionSnapshot | null
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.gesture === b.gesture &&
    sameNumbers(a.rows, b.rows) &&
    sameNumbers(a.cols, b.cols) &&
    a.cells.length === b.cells.length &&
    a.cells.every((entry, i) => entry.cell.key === b.cells[i].cell.key)
  );
};

export interface UseSelectionSurfaceParams {
  tableSlug?: string;
  onSelectionChange?: (snapshot: SpreadsheetSelectionSnapshot | null) => void;
  selectionActions?: (snapshot: SpreadsheetSelectionSnapshot | null) => SpreadsheetSelectionAction[];
  /** The datasheet watches this flag and clears its own selection when it goes true. */
  setClearSelected: (clear: boolean) => void;
  /** Sets the sheet's active cell, which the row and column indicators follow. */
  setSelectedCell: (cell: ICell | undefined) => void;
}

// Owns the committed selection for one sheet: publishes it to the page and event bus and feeds the actions bar.
const useSelectionSurface = ({
  tableSlug,
  onSelectionChange,
  selectionActions,
  setClearSelected,
  setSelectedCell,
}: UseSelectionSurfaceParams) => {
  const { publish } = useEventBus();
  const [selectionSnapshot, setSelectionSnapshot] = useState<SpreadsheetSelectionSnapshot | null>(null);
  const [fillReport, setFillReport] = useState<BulkFillReport | null>(null);
  const snapshotRef = useRef<SpreadsheetSelectionSnapshot | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  const commitSelection = useCallback(
    (snapshot: SpreadsheetSelectionSnapshot | null) => {
      if (snapshot === snapshotRef.current) {
        return;
      }
      snapshotRef.current = snapshot;
      setSelectionSnapshot(snapshot);
      const payload: SpreadsheetSelectionEvent = { snapshot, tableSlug };
      publish(EventType.SPREADSHEET_SELECTION_CHANGED, payload);
      onSelectionChangeRef.current?.(snapshot);
    },
    [publish, tableSlug]
  );

  // A rebased snapshot carries the cells' current state, so any new object refreshes the actions bar.
  // The page only hears about it when the selection moved to other cells; the event bus never does.
  const syncSelection = useCallback((snapshot: SpreadsheetSelectionSnapshot | null) => {
    const previous = snapshotRef.current;
    if (snapshot === previous) {
      return;
    }
    snapshotRef.current = snapshot;
    setSelectionSnapshot(snapshot);
    if (!isSameSelection(previous, snapshot)) {
      onSelectionChangeRef.current?.(snapshot);
    }
  }, []);

  // Drops the selection and the active cell together, so the indicators let go of the old range too.
  const clearSelection = useCallback(() => {
    setClearSelected(true);
    setSelectedCell(undefined);
    commitSelection(null);
  }, [commitSelection, setClearSelected, setSelectedCell]);

  // A fresh object per report so the bar's hide timer restarts even when the counts repeat.
  const reportBulkFill = useCallback(
    ({ filled, skipped }: BulkFillReport) => setFillReport({ filled, skipped }),
    []
  );

  const actions = useMemo(
    () => selectionActions?.(selectionSnapshot) ?? NO_ACTIONS,
    [selectionActions, selectionSnapshot]
  );

  return { selectionSnapshot, commitSelection, syncSelection, clearSelection, actions, fillReport, reportBulkFill };
};

export default useSelectionSurface;
